import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2 } from 'lucide-react';
import { theme } from '@/lib/theme';
import type { AdminService } from '@/types';
import type { ServiceFormData } from '@/hooks/useServices';

interface ServiceFormModalProps {
  open: boolean;
  editingService: AdminService | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (form: ServiceFormData) => void;
}

const emptyForm: ServiceFormData = {
  name: '', price: '', duration_minutes: '',
  short_description: '', landing_description: '', active: true,
};

export default function ServiceFormModal({ open, editingService, saving, onClose, onSubmit }: ServiceFormModalProps) {
  const [form, setForm] = useState<ServiceFormData>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof ServiceFormData, string>>>({});

  useEffect(() => {
    if (editingService) {
      setForm({
        name: editingService.name,
        price: String(editingService.price),
        duration_minutes: String(editingService.duration_minutes),
        short_description: editingService.short_description ?? '',
        landing_description: editingService.landing_description ?? editingService.description ?? '',
        booking_fee: String(editingService.booking_fee ?? 0),
        active: editingService.active,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [editingService, open]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ServiceFormData, string>> = {};
    if (!form.name.trim()) newErrors.name = 'El nombre es requerido';
    if (!form.price || isNaN(parseFloat(form.price)) || parseFloat(form.price) < 0) newErrors.price = 'Precio inválido';
    if (!form.duration_minutes || isNaN(parseInt(form.duration_minutes, 10)) || parseInt(form.duration_minutes, 10) <= 0) newErrors.duration_minutes = 'Duración inválida';
    if (form.booking_fee !== '' && (isNaN(parseFloat(form.booking_fee)) || parseFloat(form.booking_fee) < 0)) newErrors.booking_fee = 'Anticipo inválido';
    if (form.short_description.length > 120) newErrors.short_description = 'Máximo 120 caracteres';
    if (form.landing_description.length > 400) newErrors.landing_description = 'Máximo 400 caracteres';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) onSubmit(form);
  };

  const inputClass = 'w-full px-3 py-2.5 rounded-lg text-sm text-white outline-none transition-all border';
  const inputStyle = { backgroundColor: 'rgba(255,255,255,0.04)', borderColor: theme.colors.border };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            className="relative w-full max-w-lg rounded-2xl border z-10 flex flex-col max-h-[90vh]"
            style={{ backgroundColor: theme.colors.bgSurface, borderColor: theme.colors.border }}
          >
            <div className="flex items-center justify-between px-6 py-5 flex-shrink-0" style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
              <h2 className="text-white text-base font-semibold" style={{ fontFamily: theme.fonts.display }}>
                {editingService ? 'Editar servicio' : 'Nuevo servicio'}
              </h2>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/8 transition-colors cursor-pointer" style={{ color: theme.colors.textMuted }}>
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: theme.colors.textSecondary }}>Nombre del servicio <span className="text-white/60">*</span></label>
                <input type="text" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ej: Corte clásico" className={inputClass} style={inputStyle} maxLength={80} />
                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: theme.colors.textSecondary }}>Precio total (COP) <span className="text-white/60">*</span></label>
                  <input type="number" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} placeholder="0.00" min="0" step="0.01" className={inputClass} style={inputStyle} />
                  {errors.price && <p className="text-red-400 text-xs mt-1">{errors.price}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: theme.colors.textSecondary }}>Duración (min) <span className="text-white/60">*</span></label>
                  <input type="number" value={form.duration_minutes} onChange={(e) => setForm((f) => ({ ...f, duration_minutes: e.target.value }))} placeholder="40" min="1" className={inputClass} style={inputStyle} />
                  {errors.duration_minutes && <p className="text-red-400 text-xs mt-1">{errors.duration_minutes}</p>}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: theme.colors.textSecondary }}>
                  Anticipo de reserva (COP)
                  <span className="ml-1.5 font-normal" style={{ color: theme.colors.textDisabled }}>— monto cobrado al reservar en modo live</span>
                </label>
                <input
                  type="number"
                  value={form.booking_fee}
                  onChange={(e) => setForm((f) => ({ ...f, booking_fee: e.target.value }))}
                  placeholder="0"
                  min="0"
                  step="1000"
                  className={inputClass}
                  style={inputStyle}
                />
                {errors.booking_fee && <p className="text-red-400 text-xs mt-1">{errors.booking_fee}</p>}
                <p className="text-xs mt-1" style={{ color: theme.colors.textDisabled }}>
                  En modo mock siempre se registra $0 sin importar este valor.
                </p>
              </div>

              <div className="pt-1">
                <div className="h-px" style={{ backgroundColor: theme.colors.border }} />
                <p className="text-xs font-semibold uppercase tracking-wider mt-4 mb-1" style={{ color: theme.colors.textDisabled }}>Descripciones</p>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.textSecondary }}>
                  Descripción corta <span className="ml-1 font-normal" style={{ color: theme.colors.textDisabled }}>— se muestra al seleccionar el servicio en la cita</span>
                </label>
                <textarea value={form.short_description} onChange={(e) => setForm((f) => ({ ...f, short_description: e.target.value }))} placeholder="Ej: Corte preciso con tijera y máquina, acabado con navaja." rows={2} maxLength={120} className={`${inputClass} resize-none`} style={inputStyle} />
                <div className="flex items-center justify-between mt-1">
                  {errors.short_description ? <p className="text-red-400 text-xs">{errors.short_description}</p> : <span className="text-xs" style={{ color: theme.colors.textDisabled }}>Máx. 120 caracteres</span>}
                  <span className="text-xs" style={{ color: theme.colors.textDisabled }}>{form.short_description.length}/120</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: theme.colors.textSecondary }}>
                  Descripción para el landing <span className="ml-1 font-normal" style={{ color: theme.colors.textDisabled }}>— se muestra en la sección de servicios de la página principal</span>
                </label>
                <textarea value={form.landing_description} onChange={(e) => setForm((f) => ({ ...f, landing_description: e.target.value }))} placeholder="Ej: Nuestro corte clásico combina técnica tradicional con un acabado moderno." rows={4} maxLength={400} className={`${inputClass} resize-none`} style={inputStyle} />
                <div className="flex items-center justify-between mt-1">
                  {errors.landing_description ? <p className="text-red-400 text-xs">{errors.landing_description}</p> : <span className="text-xs" style={{ color: theme.colors.textDisabled }}>Máx. 400 caracteres</span>}
                  <span className="text-xs" style={{ color: theme.colors.textDisabled }}>{form.landing_description.length}/400</span>
                </div>
              </div>

              <div className="flex items-center justify-between py-1">
                <div>
                  <p className="text-sm font-medium text-white">Activo</p>
                  <p className="text-xs" style={{ color: theme.colors.textDisabled }}>Visible para los clientes en el booking</p>
                </div>
                <button type="button" onClick={() => setForm((f) => ({ ...f, active: !f.active }))} className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer flex-shrink-0 ${form.active ? 'bg-[#D9D9D9]' : 'bg-white/10'}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full transition-transform ${form.active ? 'translate-x-5 bg-black' : 'translate-x-0.5 bg-white/60'}`} />
                </button>
              </div>

              <div className="flex items-center justify-end gap-3 pt-2" style={{ borderTop: `1px solid ${theme.colors.border}` }}>
                <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium border transition-colors cursor-pointer whitespace-nowrap hover:bg-white/5" style={{ color: theme.colors.textSecondary, borderColor: theme.colors.border, backgroundColor: 'transparent' }}>
                  Cancelar
                </button>
                <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 disabled:opacity-60 hover:opacity-90" style={{ backgroundColor: theme.colors.primary, color: '#000000' }}>
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  {editingService ? 'Guardar cambios' : 'Crear servicio'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
